import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Pin,
  PinOff,
  Search,
  Users,
} from 'lucide-react';
import { apiClient } from '@/lib/api';
import { useAutoCollapse } from '@/hooks/useAutoCollapse';
import { cn } from '@/lib/utils';

type WorkspaceTab = 'tasks' | 'leads' | 'calendar';

interface W3TaskItem {
  id: string;
  title: string;
  code?: string | null;
  priority?: string | null;
  due_date?: string | null;
  status_name?: string | null;
  status_color?: string | null;
  is_completed?: boolean | null;
  created_at?: string;
}

interface W3LeadItem {
  id: string;
  title?: string | null;
  contact_first_name?: string | null;
  contact_last_name?: string | null;
  company_name?: string | null;
  source?: string | null;
  stage_name?: string | null;
  stage_color?: string | null;
  score_band?: 'hot' | 'warm' | 'cold' | null;
  created_at?: string;
}

interface LeadsListResponse {
  leads: W3LeadItem[];
  total: number;
}

type MonthCountsMap = Record<string, number>;

interface DayAppointment {
  id: string;
  title?: string | null;
  service_name?: string | null;
  customer_name?: string | null;
  start_time: string;
  end_time?: string | null;
}

const TABS: ReadonlyArray<{ id: WorkspaceTab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: 'tasks', label: 'Tasks', icon: CheckSquare },
  { id: 'leads', label: 'Leads', icon: Users },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
];

const MONTH_LABELS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
] as const;

const DAY_LABELS = ['L', 'M', 'M', 'G', 'V', 'S', 'D'] as const;

function toDateKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function isToday(dateKey: string): boolean {
  return dateKey === toDateKey(new Date());
}

function leadDisplayName(lead: W3LeadItem): string {
  const person = [lead.contact_first_name, lead.contact_last_name].filter(Boolean).join(' ');
  return lead.title || person || lead.company_name || 'Lead senza nome';
}

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return `${Math.max(mins, 1)} min fa`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h fa`;
  return `${Math.floor(hours / 24)}g fa`;
}

interface PriorityStyle {
  border: string;
  label: string;
}

const PRIORITY_FALLBACK: PriorityStyle = { border: 'border-l-slate-300', label: '' };

const PRIORITY_STYLES: Record<string, PriorityStyle> = {
  urgent_important: { border: 'border-l-red-500', label: 'Urgente' },
  not_urgent_important: { border: 'border-l-amber-500', label: 'Importante' },
  urgent_not_important: { border: 'border-l-blue-500', label: 'Urgente' },
  not_urgent_not_important: { border: 'border-l-slate-300', label: 'Normale' },
  none: { border: 'border-l-slate-300', label: '' },
};

const BAND_STYLES: Record<NonNullable<W3LeadItem['score_band']>, { border: string; badge: string; label: string }> = {
  hot: { border: 'border-l-red-500', badge: 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300', label: 'Alta' },
  warm: { border: 'border-l-amber-500', badge: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300', label: 'Media' },
  cold: { border: 'border-l-blue-400', badge: 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300', label: 'Bassa' },
};

function StatCard({ label, value, tone }: { label: string; value: number; tone: 'orange' | 'green' | 'red' | 'blue' }) {
  const tones: Record<typeof tone, string> = {
    orange: 'text-w3-orange',
    green: 'text-emerald-600 dark:text-emerald-400',
    red: 'text-red-600 dark:text-red-400',
    blue: 'text-blue-600 dark:text-blue-400',
  };
  return (
    <div className="w3-glass-panel rounded-xl p-3 flex-1 min-w-0">
      <p className={cn('text-lg font-bold tabular-nums', tones[tone])}>{value}</p>
      <p className="text-[11px] text-muted-foreground truncate">{label}</p>
    </div>
  );
}

/**
 * Pannello workspace destro (conforme al Layout demo W3): tab Tasks / Leads /
 * Calendar con dati reali; collassabile a rail icone, auto-collapse dopo 30s
 * di inattività, pin che blocca l'espansione.
 */
export function WorkspacePanel() {
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('tasks');
  const [taskSearch, setTaskSearch] = useState('');
  const [calCursor, setCalCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  const [selectedDate, setSelectedDate] = useState<string>(() => toDateKey(new Date()));
  const { collapsed, pinned, togglePinned, touch, expand } = useAutoCollapse('w3.workspace.pinned');

  const { data: tasks = [] } = useQuery<W3TaskItem[]>({
    queryKey: ['w3-workspace-tasks', taskSearch],
    queryFn: async () => {
      const qs = new URLSearchParams({ limit: '25' });
      if (taskSearch.trim()) qs.set('search', taskSearch.trim());
      const res = await apiClient.get<W3TaskItem[]>(`/tasks?${qs.toString()}`);
      return res.data ?? [];
    },
    staleTime: 60 * 1000,
    refetchInterval: 120_000,
  });

  const { data: leadsResponse } = useQuery<LeadsListResponse>({
    queryKey: ['w3-workspace-leads'],
    queryFn: async () => {
      // La lista CRM risponde senza wrapper {success,data}: cast tipizzato al boundary.
      const res = (await apiClient.get<unknown>('/crm/leads?limit=20&order=score')) as unknown as LeadsListResponse;
      return { leads: Array.isArray(res.leads) ? res.leads : [], total: res.total ?? 0 };
    },
    staleTime: 60 * 1000,
    refetchInterval: 120_000,
  });

  const { data: monthCounts = {} } = useQuery<MonthCountsMap>({
    queryKey: ['w3-workspace-cal-month', calCursor.year, calCursor.month],
    queryFn: async () => {
      const res = await apiClient.get<MonthCountsMap>(
        `/calendar/appointments/month/${calCursor.year}/${calCursor.month}`
      );
      return res.data ?? {};
    },
    staleTime: 60 * 1000,
    enabled: activeTab === 'calendar' && !collapsed,
  });

  const { data: dayAppointments = [] } = useQuery<DayAppointment[]>({
    queryKey: ['w3-workspace-cal-day', selectedDate],
    queryFn: async () => {
      const res = await apiClient.get<DayAppointment[]>(`/calendar/appointments/date/${selectedDate}`);
      return res.data ?? [];
    },
    staleTime: 30 * 1000,
    enabled: activeTab === 'calendar' && !collapsed,
  });

  const leads = leadsResponse?.leads ?? [];
  const todayKey = toDateKey(new Date());

  const taskStats = useMemo(() => {
    const todayCount = tasks.filter((t) => t.due_date && t.due_date.slice(0, 10) === todayKey && !t.is_completed).length;
    const completed = tasks.filter((t) => t.is_completed === true).length;
    return { today: todayCount, completed };
  }, [tasks, todayKey]);

  const leadStats = useMemo(() => {
    const high = leads.filter((l) => l.score_band === 'hot').length;
    return { high, active: leads.length };
  }, [leads]);

  const openTasks = useMemo(() => tasks.filter((t) => t.is_completed !== true).slice(0, 8), [tasks]);

  const eventCountByDate = useMemo(() => {
    const map = new Map<string, number>();
    for (const [date, count] of Object.entries(monthCounts)) {
      const n = typeof count === 'number' ? count : parseInt(String(count), 10);
      map.set(date, Number.isNaN(n) ? 0 : n);
    }
    return map;
  }, [monthCounts]);

  const calendarCells = useMemo(() => {
    const first = new Date(calCursor.year, calCursor.month - 1, 1);
    const daysInMonth = new Date(calCursor.year, calCursor.month, 0).getDate();
    // Lunedì = prima colonna.
    const leading = (first.getDay() + 6) % 7;
    const cells: Array<{ key: string; day: number } | null> = [];
    for (let i = 0; i < leading; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push({ key: toDateKey(new Date(calCursor.year, calCursor.month - 1, d)), day: d });
    }
    return cells;
  }, [calCursor]);

  const tabBadge = (tab: WorkspaceTab): number => {
    if (tab === 'tasks') return openTasks.length;
    if (tab === 'leads') return leads.length;
    return eventCountByDate.get(todayKey) ?? 0;
  };

  const moveMonth = (delta: number) => {
    setCalCursor((prev) => {
      const d = new Date(prev.year, prev.month - 1 + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });
  };

  return (
    <aside
      className={cn(
        'hidden xl:flex flex-col shrink-0 transition-all duration-300 border-l border-border bg-card/60',
        collapsed ? 'w-12' : 'w-80'
      )}
      onMouseMove={collapsed ? undefined : touch}
      onKeyDown={collapsed ? undefined : touch}
    >
      {/* Header */}
      <div className={cn('flex items-center h-16 border-b border-border shrink-0', collapsed ? 'justify-center' : 'justify-between px-4')}>
        {!collapsed && (
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-foreground truncate">Workspace</span>
            <button
              type="button"
              onClick={togglePinned}
              className={cn(
                'p-1.5 rounded-md transition-colors',
                pinned
                  ? 'text-w3-orange bg-w3-orange/10 hover:bg-w3-orange/20'
                  : 'text-muted-foreground hover:text-w3-orange hover:bg-w3-orange/10'
              )}
              title={pinned ? 'Sblocca (auto-collapse attivo)' : 'Blocca aperto'}
              aria-label={pinned ? 'Sblocca workspace' : 'Blocca workspace aperto'}
            >
              {pinned ? <Pin className="w-3.5 h-3.5" /> : <PinOff className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={() => (collapsed ? expand() : undefined)}
          className={cn('p-1.5 rounded-md text-muted-foreground hover:text-w3-orange hover:bg-w3-orange/10 transition-colors', !collapsed && 'hidden')}
          aria-label="Espandi workspace"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        {!collapsed && !pinned && (
          <button
            type="button"
            onClick={() => touch()}
            className="sr-only"
            aria-hidden="true"
            tabIndex={-1}
          />
        )}
      </div>

      {collapsed ? (
        // Rail: icone tab con badge conteggi (come demo)
        <div className="flex flex-col items-center gap-2 py-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const badge = tabBadge(tab.id);
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                  expand();
                }}
                className={cn(
                  'relative p-2 rounded-lg transition-colors',
                  activeTab === tab.id
                    ? 'text-w3-orange bg-w3-orange/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )}
                title={tab.label}
                aria-label={tab.label}
              >
                <Icon className="w-4 h-4" />
                {badge > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full w3-gradient-brand text-white text-[9px] font-bold flex items-center justify-center">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 animate-fade-in">
          {/* Tab pills */}
          <div className="flex gap-1 p-3 pb-2 shrink-0">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-all',
                    activeTab === tab.id
                      ? 'w3-gradient-brand text-white shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-3">
            {activeTab === 'tasks' && (
              <>
                <div className="flex gap-2">
                  <StatCard label="Oggi" value={taskStats.today} tone="orange" />
                  <StatCard label="Completate" value={taskStats.completed} tone="green" />
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <input
                    type="text"
                    value={taskSearch}
                    onChange={(e) => setTaskSearch(e.target.value)}
                    placeholder="Cerca attività…"
                    className="w-full pl-8 pr-3 py-1.5 rounded-lg text-xs bg-muted/60 border border-border focus:outline-none focus:ring-2 focus:ring-inset focus:ring-w3-orange"
                  />
                </div>
                <div className="space-y-1.5">
                  {openTasks.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-4 text-center">Nessuna attività aperta.</p>
                  ) : (
                    openTasks.map((task) => {
                      const prio = PRIORITY_STYLES[task.priority ?? 'none'] ?? PRIORITY_FALLBACK;
                      return (
                        <div
                          key={task.id}
                          className={cn('w3-glass-panel rounded-lg p-2.5 border-l-2', prio.border)}
                        >
                          <p className="text-xs font-medium truncate">{task.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {task.status_name && (
                              <span className="text-[10px] text-muted-foreground">{task.status_name}</span>
                            )}
                            {task.due_date && (
                              <span
                                className={cn(
                                  'text-[10px] tabular-nums',
                                  task.due_date.slice(0, 10) < todayKey
                                    ? 'text-red-600 dark:text-red-400 font-medium'
                                    : 'text-muted-foreground'
                                )}
                              >
                                {new Date(task.due_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                            {prio.label && (
                              <span className="text-[10px] text-muted-foreground ml-auto">{prio.label}</span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}

            {activeTab === 'leads' && (
              <>
                <div className="flex gap-2">
                  <StatCard label="Priorità Alta" value={leadStats.high} tone="red" />
                  <StatCard label="Contatti Attivi" value={leadStats.active} tone="blue" />
                </div>
                <div className="space-y-1.5">
                  {leads.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-4 text-center">Nessun lead recente.</p>
                  ) : (
                    leads.slice(0, 8).map((lead) => {
                      const band = lead.score_band ? BAND_STYLES[lead.score_band] : null;
                      return (
                        <div
                          key={lead.id}
                          className={cn('w3-glass-panel rounded-lg p-2.5 border-l-2', band?.border ?? 'border-l-slate-300')}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-medium truncate">{leadDisplayName(lead)}</p>
                            {band && (
                              <span className={cn('shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-semibold', band.badge)}>
                                {band.label}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                            {lead.stage_name && <span className="truncate">{lead.stage_name}</span>}
                            {lead.source && <span className="truncate">· {lead.source}</span>}
                            <span className="ml-auto shrink-0">{relativeTime(lead.created_at)}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}

            {activeTab === 'calendar' && (
              <>
                {/* Mini-calendario con navigazione mesi */}
                <div className="w3-glass-panel rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      type="button"
                      onClick={() => moveMonth(-1)}
                      className="p-1 rounded-md text-muted-foreground hover:text-w3-orange hover:bg-w3-orange/10"
                      aria-label="Mese precedente"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-semibold">
                      {MONTH_LABELS[calCursor.month - 1]} {calCursor.year}
                    </span>
                    <button
                      type="button"
                      onClick={() => moveMonth(1)}
                      className="p-1 rounded-md text-muted-foreground hover:text-w3-orange hover:bg-w3-orange/10"
                      aria-label="Mese successivo"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-0.5 text-center">
                    {DAY_LABELS.map((d, i) => (
                      <span key={`${d}-${i}`} className="text-[9px] font-medium text-muted-foreground py-1">
                        {d}
                      </span>
                    ))}
                    {calendarCells.map((cell, i) =>
                      cell === null ? (
                        <span key={`empty-${i}`} />
                      ) : (
                        <button
                          key={cell.key}
                          type="button"
                          onClick={() => setSelectedDate(cell.key)}
                          className={cn(
                            'relative aspect-square rounded-md text-[10px] tabular-nums transition-colors',
                            selectedDate === cell.key
                              ? 'w3-gradient-brand text-white font-semibold'
                              : isToday(cell.key)
                                ? 'text-w3-orange font-semibold hover:bg-w3-orange/10'
                                : 'text-foreground hover:bg-muted'
                          )}
                        >
                          {cell.day}
                          {(eventCountByDate.get(cell.key) ?? 0) > 0 && selectedDate !== cell.key && (
                            <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-w3-orange" />
                          )}
                        </button>
                      )
                    )}
                  </div>
                </div>

                {/* Eventi del giorno selezionato */}
                <div className="space-y-1.5">
                  <p className="text-[11px] font-semibold text-muted-foreground">
                    {new Date(`${selectedDate}T00:00:00`).toLocaleDateString('it-IT', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    })}
                  </p>
                  {dayAppointments.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-3 text-center">Nessun evento in agenda.</p>
                  ) : (
                    dayAppointments.map((appt) => (
                      <div key={appt.id} className="w3-glass-panel rounded-lg p-2.5 border-l-2 border-l-w3-purple">
                        <p className="text-xs font-medium truncate">
                          {appt.title || appt.service_name || appt.customer_name || 'Appuntamento'}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                          {new Date(appt.start_time).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                          {appt.end_time &&
                            ` – ${new Date(appt.end_time).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}
